import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiArrowLeft, FiDownload, FiEdit2, FiTrash2, FiFile, FiShare2, FiStar, FiUpload, FiClock, FiX } from 'react-icons/fi';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFileArchive,
  FaFileAlt,
  FaStar
} from 'react-icons/fa';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import FileViewer from 'react-file-viewer';
import axiosInstance from '../../services/AxiosInstance';
import { formatDateToDDMMMYYYY, formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import { navigateBackSafely } from '../../utils/navigateBackSafely';
import { pickQuerySuffix, withListQuery } from '../../utils/listQueryPersistence';
import { buildDocumentSharePayload, mergeUsersById, shareUserId } from '../../utils/documentSharePayload';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import DocumentShareForm from './DocumentShareForm';
import ShareDocumentModal from './ShareDocumentModal';
import { useDocumentAccess } from '../../hooks/useDocumentAccess';
import { useCanDo } from '../../hooks/useCanDo';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import '../tasks/taskdetails.css';
import '../forum/forum.css';
import './documentDetails.css';
import './commonstyle.css';

const DOCUMENT_LIST_QUERY_KEYS = ['search', 'sort', 'cat', 'branch', 'dept', 'sub'];

const DocumentDetails = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess, loading: accessLoading } = useDocumentAccess();
  const canEditDocuments = useCanDo('documents', 'create_edit');
  const canDeleteDocuments = useCanDo('documents', 'delete');
  const canShareDocuments = useCanDo('documents', 'share');
  const canDownloadDocuments = useCanDo('documents', 'download');

  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [userdata, setUserdata] = useState(null);
  // Share form state
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedSubDepartments, setSelectedSubDepartments] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [sharing, setSharing] = useState(false);
  // Upload version state
  const [showUploadVersionModal, setShowUploadVersionModal] = useState(false);
  const [uploadVersionFile, setUploadVersionFile] = useState(null);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  // Upload version form fields
  const [versionDocumentTitle, setVersionDocumentTitle] = useState('');
  const [versionRegisteredTo, setVersionRegisteredTo] = useState('');
  const [versionReferenceNumber, setVersionReferenceNumber] = useState('');
  const [versionIssueDate, setVersionIssueDate] = useState('');
  const [versionExpiryDate, setVersionExpiryDate] = useState('');
  const [versionRemindBefore, setVersionRemindBefore] = useState('');
  const [versionIssuingAuthority, setVersionIssuingAuthority] = useState('');
  const [versionOtherComments, setVersionOtherComments] = useState('');
  // Document preview state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [classificationPath, setClassificationPath] = useState([]);
  // Document shares state
  const [documentShares, setDocumentShares] = useState(null);
  const [loadingShares, setLoadingShares] = useState(false);
  const [revokingShare, setRevokingShare] = useState(false);
  const [showRevokeAllModal, setShowRevokeAllModal] = useState(false);
  const [revokingAllShares, setRevokingAllShares] = useState(false);
  const [showRevokeShareModal, setShowRevokeShareModal] = useState(false);
  const [revokeShareData, setRevokeShareData] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
  });

  const notifyError = (msg) => toast.error(`❌ ${msg}`, {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });

  const trackDocumentView = async (docId) => {
    if (!docId) return;

    try {
      // Track document view - don't wait for response or show errors to user
      await axiosInstance.post(`/documents/${docId}/view`);
    } catch (error) {
      // Silently fail - view tracking shouldn't interrupt user experience
      console.log('Error tracking document view:', error);
    }
  };

  const fetchDocumentDetails = useCallback(async () => {
    if (!documentId) return;

    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;

      // Try to fetch document details - do not fall back to list on auth/permission errors
      let response;
      try {
        response = await axiosInstance.get(`/documents/${documentId}`);
      } catch (error) {
        const status = error.response?.status;
        if (status === 401 || status === 403 || status === 409 || status === 404) {
          throw error;
        }
        // Fallback only for unexpected failures (e.g. missing endpoint)
        const params = new URLSearchParams();
        params.append('corporate_id', corporateId);
        const listResponse = await axiosInstance.get(`/documents/list?${params.toString()}`);

        if (listResponse.status === 200 && listResponse.data.success) {
          const documents = listResponse.data.data.documents || [];
          const document = documents.find(doc => doc.id === parseInt(documentId));

          if (document) {
            // Format document data
            const formattedDocument = {
              id: document.id,
              name: document.name || document.file_name,
              file_name: document.name || document.file_name,
              file_size: document.file_size,
              size: document.file_size ? `${(parseFloat(document.file_size) / 1024 / 1024).toFixed(2)} MB` : '0 MB',
              file_type: document.file_type,
              file_url: document.file_url || null,
              version: document.version || 1,
              is_private: document.is_private || false,
              category_id: document.category?.id || null,
              category_name: document.category?.name || null,
              created_at: document.created_at,
              updated_at: document.updated_at,
              created_by: document.created_by || null,
              uploaded_by: document.uploaded_by || document.created_by || null,
              uploaded_by_user: document.uploaded_by_user || null,
              created_by_name: document?.uploaded_by_user?.full_name || null,
              document_path_id: document.document_path_id,
              folder_name: document.folder?.name || null,
              description: document.description || null,
              branches: document.branches || [],
              departments: document.departments || [],
              sub_departments: document.sub_departments || [],
              versions: document.versions || document.version_history || [],
              // New document fields
              document_title: document.document_title || null,
              registered_to: document.registered_to || null,
              reference_number: document.reference_number || null,
              issue_date: document.issue_date || null,
              expiry_date: document.expiry_date || null,
              remind_before: document.remind_before || null,
          issuing_authority: document.issuing_authority || null,
          other_comments: document.other_comments || null,
          is_favorite: document.is_favorite || false,
          // Linked documents fields
          linked_staff: document.linked_staff || null,
          staff_id: document.staff_id || null,
          linked_vehicle: document.linked_vehicle || null,
          vehicle_id: document.vehicle_id || null,
          is_linked_to_staff: document.is_linked_to_staff || document.link_to_staff || false,
          is_linked_to_vehicle: document.is_linked_to_vehicle || document.link_to_vehicle || false,
        };
        setDocumentData(formattedDocument);
        // Set version history if available
        if (document.versions || document.version_history) {
          setVersionHistory(document.versions || document.version_history || []);
        }
            setLoading(false);
            // Track document view after successful load
            trackDocumentView(document.id);
            return;
          }
        }
        throw error;
      }

      if (response && response.status === 200) {
        const data = response.data;
        const document = data.data || data;

        // Format document data
        const formattedDocument = {
          id: document.id,
          name: document.name || document.file_name,
          file_name: document.name || document.file_name,
          file_size: document.file_size,
          size: document.file_size ? `${(parseFloat(document.file_size) / 1024 / 1024).toFixed(2)} MB` : '0 MB',
          file_type: document.file_type,
          version: document.version || 1,
          is_private: document.is_private || false,
          category_id: document.category?.id || null,
          category_name: document.category?.name || null,
          created_at: document.created_at,
          updated_at: document.updated_at,
          created_by: document.created_by || null,
          uploaded_by: document.uploaded_by || document.created_by || null,
          uploaded_by_user: document.uploaded_by_user || null,
          created_by_name: document?.uploaded_by_user?.full_name || null,
          document_path_id: document.document_path_id,
          folder_name: document.folder?.name || null,
          description: document.description || null,
          branches: document.branches || [],
          departments: document.departments || [],
          sub_departments: document.sub_departments || [],
          versions: document.versions || document.version_history || [],
          file_url: document.file_url || null,
          // New document fields
          document_title: document.document_title || null,
          registered_to: document.registered_to || null,
          reference_number: document.reference_number || null,
          issue_date: document.issue_date || null,
          expiry_date: document.expiry_date || null,
          remind_before: document.remind_before || null,
          issuing_authority: document.issuing_authority || null,
          other_comments: document.other_comments || null,
          is_favorite: document.is_favorite || false,
          // Linked documents fields
          linked_staff: document.linked_staff || null,
          staff_id: document.staff_id || null,
          linked_vehicle: document.linked_vehicle || null,
          vehicle_id: document.vehicle_id || null,
          is_linked_to_staff: document.is_linked_to_staff || document.link_to_staff || false,
          is_linked_to_vehicle: document.is_linked_to_vehicle || document.link_to_vehicle || false,
        };
        setDocumentData(formattedDocument);
        // Set version history if available
        if (document.versions || document.version_history) {
          setVersionHistory(document.versions || document.version_history || []);
        }
        // Track document view after successful load
        trackDocumentView(document.id);
      }
    } catch (error) {
      console.error('Error fetching document details:', error);
      const status = error.response?.status;

      if (status === 403 || status === 409) {
        try { sessionStorage.removeItem('redirectAfterLogin'); } catch (e) { /* ignore */ }
        notifyError(
          error.response?.data?.message ||
          "Access denied. You don't have permission to view this document."
        );
        setTimeout(() => {
          setLoading(false);
          navigate('/documents/dashboard');
        }, 2000);
        return;
      }

      if (status === 404) {
        try { sessionStorage.removeItem('redirectAfterLogin'); } catch (e) { /* ignore */ }
        notifyError(error.response?.data?.message || 'Document not found.');
        setTimeout(() => {
          setLoading(false);
          navigate('/documents/dashboard');
        }, 2000);
        return;
      }

      // Handle 500 errors specifically - might be category-related, but don't block document view
      if (status === 500) {
        const errorMessage = error.response?.data?.message || 'Server error loading document details';
        console.warn('Server error (possibly category-related):', errorMessage);
        notifyError('Some document information may be incomplete due to a server error');
      } else {
        notifyError(error.response?.data?.message || 'Failed to load document details');
      }
    } finally {
      setLoading(false);
    }
  }, [documentId, navigate]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUserdata(userData);

    if (documentId) {
      fetchDocumentDetails();
    }
  }, [documentId, fetchDocumentDetails]);

  // Check if current user is the uploader
  const isUploader = useCallback(() => {
    if (!userdata || !documentData) return false;
    const currentUserId = userdata.id || userdata.corporateUserId;
    const uploadedBy = documentData.uploaded_by || documentData.created_by;

    // Also check uploaded_by_user.id if available
    if (documentData.uploaded_by_user?.id) {
      return parseInt(currentUserId) === parseInt(documentData.uploaded_by_user.id);
    }

    return uploadedBy && parseInt(currentUserId) === parseInt(uploadedBy);
  }, [userdata, documentData]);

  // Fetch document shares
  const rememberShareUsers = useCallback((users) => {
    if (!users?.length) return;
    setAvailableUsers((prev) => mergeUsersById(prev, users));
    setFilteredUsers((prev) => mergeUsersById(prev, users));
    setSelectedUsers(users.map(shareUserId).filter((id) => id != null && id !== ''));
  }, []);

  const fetchDocumentShares = useCallback(async () => {
    if (!documentId) return;

    try {
      setLoadingShares(true);
      const response = await axiosInstance.get(`/documents/${documentId}/shares`);

      if (response.status === 200 && response.data.success) {
        const payload = response.data.data;
        setDocumentShares(payload);
        const shares = payload?.shares;
        if (shares) {
          const branchIds = (shares.branches || []).map((b) => b.id || b.branch_id).filter(Boolean);
          const departmentIds = (shares.departments || []).map((d) => d.id || d.department_id).filter(Boolean);
          const subDepartmentIds = (shares.sub_departments || []).map((sd) => sd.id || sd.sub_department_id).filter(Boolean);
          if (branchIds.length) setSelectedBranches(branchIds);
          if (departmentIds.length) setSelectedDepartments(departmentIds);
          if (subDepartmentIds.length) setSelectedSubDepartments(subDepartmentIds);
          if ((shares.users || []).length) rememberShareUsers(shares.users);
        }
      }
    } catch (error) {
      console.error('Error fetching document shares:', error);
      // Don't show error to user if endpoint doesn't exist or user doesn't have permission
      if (error.response?.status !== 404 && error.response?.status !== 403) {
        notifyError(error.response?.data?.message || 'Failed to load document shares');
      }
    } finally {
      setLoadingShares(false);
    }
  }, [documentId, rememberShareUsers]);

  // Load shares for the uploader and for anyone the document was shared with.
  useEffect(() => {
    if (documentData) {
      fetchDocumentShares();
    }
  }, [documentData, fetchDocumentShares]);

  // Open revoke share confirmation modal
  const handleRevokeShareClick = (shareType, shareId, shareName) => {
    setRevokeShareData({ shareType, shareId, shareName });
    setShowRevokeShareModal(true);
  };

  // Revoke a share
  const handleRevokeShare = async () => {
    if (!documentData || !documentData.id || !revokeShareData) {
      notifyError('Invalid document or share data');
      return;
    }

    const { shareType, shareId } = revokeShareData;

    try {
      setRevokingShare(true);

      const payload = {
        branch_ids: [],
        department_ids: [],
        sub_department_ids: [],
        user_ids: []
      };

      // Set the appropriate field based on share type
      switch (shareType) {
        case 'branch':
          payload.branch_ids = [shareId];
          break;
        case 'department':
          payload.department_ids = [shareId];
          break;
        case 'sub_department':
          payload.sub_department_ids = [shareId];
          break;
        case 'user':
          payload.user_ids = [shareId];
          break;
        default:
          notifyError('Invalid share type');
          return;
      }

      const response = await axiosInstance.delete(`/documents/${documentData.id}/share`, {
        data: payload
      });

      if (response.status === 200 || response.status === 204) {
        notifySuccess('Share revoked successfully');
        // Refresh shares list
        await fetchDocumentShares();
        setShowRevokeShareModal(false);
        setRevokeShareData(null);
      }
    } catch (error) {
      console.error('Error revoking share:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to revoke share';
      notifyError(errorMessage);
    } finally {
      setRevokingShare(false);
    }
  };

  // Revoke all shares
  const handleRevokeAllShares = async () => {
    if (!documentData || !documentData.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setRevokingAllShares(true);

      // Collect all share IDs
      const payload = {
        branch_ids: documentShares?.shares?.branches?.map(b => b.id) || [],
        department_ids: documentShares?.shares?.departments?.map(d => d.id) || [],
        sub_department_ids: documentShares?.shares?.sub_departments?.map(sd => sd.id) || [],
        user_ids: documentShares?.shares?.users?.map(u => u.id) || []
      };

      const response = await axiosInstance.delete(`/documents/${documentData.id}/share`, {
        data: payload
      });

      if (response.status === 200 || response.status === 204) {
        notifySuccess('All shares revoked successfully');
        setShowRevokeAllModal(false);
        // Refresh shares list
        await fetchDocumentShares();
      }
    } catch (error) {
      console.error('Error revoking all shares:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to revoke all shares';
      notifyError(errorMessage);
    } finally {
      setRevokingAllShares(false);
    }
  };

  // Fetch full folder/classification path (root → leaf) for "Uploaded under"
  const fetchClassificationPath = useCallback(async (categoryId, fallbackCategoryName = null) => {
    setClassificationPath(null); // loading state

    if (!categoryId) {
      setClassificationPath([]); // no category
      return;
    }

    try {
      const path = [];
      let current = categoryId;
      const seen = new Set();

      while (current && !seen.has(current)) {
        seen.add(current);
        const res = await axiosInstance.get(`/document-categories/${current}`);
        const category = res.data.data || res.data;

        if (!category) break;

        path.unshift({
          id: category.id,
          name: category.name
        });

        current = category.parent_id || null;
      }

      if (path.length > 0) {
        setClassificationPath(path);
        return;
      }

      // no parents found
      if (fallbackCategoryName) {
        setClassificationPath([{ id: categoryId, name: fallbackCategoryName }]);
      } else {
        setClassificationPath([]);
      }
    } catch (err) {

      if (err.response?.status === 500) {
        notifyError(err.response?.data?.message || "You cannot access this category.");
        setClassificationPath("ACCESS_DENIED");
        return;
      }

      console.error("Error fetching classification path:", err);

      if (fallbackCategoryName) {
        setClassificationPath([{ id: categoryId, name: fallbackCategoryName }]);
      } else {
        setClassificationPath([]);
      }
    }
  }, [axiosInstance]);

  // Fetch classification path when document data or category changes
  useEffect(() => {
    if (documentData?.category_id) {
      fetchClassificationPath(documentData.category_id, documentData.category_name);
    } else {
      setClassificationPath([]);
    }
  }, [documentData?.category_id, documentData?.category_name, fetchClassificationPath]);

  // Fetch file as blob to avoid CORS issues
  useEffect(() => {
    if (!documentData || !documentData.file_url) {
      setPreviewUrl(null);
      return;
    }

    const fetchFileForPreview = async () => {
      try {
        setPreviewError(null);

        // Get the file URL
        let fileUrl = documentData.file_url;

        // If file_url is relative, prepend the API base URL
        if (fileUrl && !fileUrl.startsWith('http://') && !fileUrl.startsWith('https://') && !fileUrl.startsWith('blob:')) {
          const baseUrl = process.env.REACT_APP_API_URL || '';
          const cleanBaseUrl = baseUrl.replace(/\/$/, '');
          const cleanFileUrl = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
          fileUrl = `${cleanBaseUrl}${cleanFileUrl}`;
        }

        // Fetch the file as blob using authenticated request to avoid CORS issues
        const response = await axiosInstance.get(fileUrl, {
          responseType: 'blob',
          headers: {
            'Accept': 'application/octet-stream'
          }
        });

        if (response.status === 200) {
          // Get the content type from response headers
          const contentType = response.headers['content-type'] ||
            response.headers['Content-Type'] ||
            'application/pdf';

          console.log('File fetched successfully:', {
            contentType,
            size: response.data.size,
            type: response.data.type
          });

          // Create blob URL with proper MIME type
          const blob = new Blob([response.data], { type: contentType });
          const blobUrl = window.URL.createObjectURL(blob);
          console.log('Blob URL created:', blobUrl);
          setPreviewUrl(blobUrl);
        }
      } catch (error) {
        console.error('Error fetching file for preview:', error);
        setPreviewError('Failed to load file for preview. Please try downloading the file.');
        setPreviewUrl(null);
      }
    };

    fetchFileForPreview();

    // Cleanup: revoke blob URL when component unmounts or document changes
    return () => {
      setPreviewUrl((currentUrl) => {
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        return null;
      });
    };
  }, [documentData?.file_url]);

  // Handle file viewer errors - prevent page crash
  const onError = (e) => {
    console.error('Error loading file preview:', e);
    console.log('File URL:', previewUrl);
    console.log('File Type:', documentData?.file_type);
    console.log('File Name:', documentData?.name || documentData?.file_name);

    // Set error state to show error message instead of crashing
    try {
      setPreviewError('Failed to load file preview. The file type may not be supported. Please try downloading the file.');
      setPreviewUrl(null); // Clear preview URL to prevent further errors
    } catch (error) {
      console.error('Error setting preview error state:', error);
    }
  };

  // Fetch data for share form once on mount
  useEffect(() => {
    fetchBranches();
    fetchDepartments();
    fetchSubDepartments();
    fetchUsers();
  }, []);

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;

      const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
      const data = res.data;

      if (res) {
        setBranches(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // Fetch departments
  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;

      const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
      const data = res.data;

      if (res) {
        const deptList = data.data || data || [];
        setDepartments(deptList);
        setFilteredDepartments(deptList);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Fetch sub-departments
  const fetchSubDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;

      let res;
      try {
        res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`);
      } catch (e1) {
        res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`);
      }
      const data = res.data;

      if (res) {
        const subDeptList = data.data || data || [];
        setSubDepartments(subDeptList);
        setFilteredSubDepartments(subDeptList);
      }
    } catch (error) {
      console.error('Error fetching sub-departments:', error);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;

      if (!corporateId) return;

      const params = new URLSearchParams();
      params.append('corporateId', parseInt(corporateId));

      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];

      // Keep people already on the share, including users with no branch or department
      // and the current user when the document was shared directly with them.
      setAvailableUsers((prev) => mergeUsersById(users, prev));
      setFilteredUsers((prev) => mergeUsersById(users, prev));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Handle branch change - filter departments
  const handleBranchChange = async (branchIds, selectedOptions) => {
    setSelectedBranches(branchIds);

    if (branchIds.length > 0) {
      // Filter departments based on selected branches
      const filtered = departments.filter(dept => branchIds.includes(dept.branch_id));
      setFilteredDepartments(filtered);

      // Keep only valid departments
      const validDepartmentIds = selectedDepartments.filter(deptId => {
        return filtered.some(d => d.id === deptId);
      });
      setSelectedDepartments(validDepartmentIds);

      // Filter sub-departments based on valid departments
      let subs = [];
      if (validDepartmentIds.length > 0) {
        subs = subDepartments.filter(sd => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          return validDepartmentIds.includes(sdDeptId);
        });
      } else {
        subs = subDepartments.filter(sd => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          const dept = departments.find(d => d.id === sdDeptId);
          return dept && branchIds.includes(dept.branch_id);
        });
      }
      setFilteredSubDepartments(subs);

      // Keep only valid sub-departments
      const validSubDepartmentIds = selectedSubDepartments.filter(subDeptId => {
        return subs.some(sd => {
          const sdId = sd.id || sd.sub_department_id || sd.subDepartmentId;
          return sdId === subDeptId;
        });
      });
      setSelectedSubDepartments(validSubDepartmentIds);

      // Filter users based on selected branches, departments, and sub-departments
      await filterUsersBySelections(branchIds, validDepartmentIds, validSubDepartmentIds);
    } else {
      setFilteredDepartments(departments);

      if (selectedDepartments.length > 0) {
        const subs = subDepartments.filter(sd => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          return selectedDepartments.includes(sdDeptId);
        });
        setFilteredSubDepartments(subs);

        const validSubDepartmentIds = selectedSubDepartments.filter(subDeptId => {
          return subs.some(sd => {
            const sdId = sd.id || sd.sub_department_id || sd.subDepartmentId;
            return sdId === subDeptId;
          });
        });
        setSelectedSubDepartments(validSubDepartmentIds);

        await filterUsersBySelections([], selectedDepartments, validSubDepartmentIds);
      } else {
        setFilteredSubDepartments(subDepartments);
        setFilteredUsers(availableUsers);
      }
    }
  };

  // Handle department change - filter sub-departments
  const handleDepartmentChange = async (departmentIds, selectedOptions) => {
    setSelectedDepartments(departmentIds);

    if (departmentIds.length > 0) {
      let subs = subDepartments.filter(sd => {
        const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
        return departmentIds.includes(sdDeptId);
      });

      if (selectedBranches.length > 0) {
        subs = subs.filter(sd => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          const dept = departments.find(d => d.id === sdDeptId);
          return dept && selectedBranches.includes(dept.branch_id);
        });
      }

      setFilteredSubDepartments(subs);

      const validSubDepartmentIds = selectedSubDepartments.filter(subDeptId => {
        return subs.some(sd => {
          const sdId = sd.id || sd.sub_department_id || sd.subDepartmentId;
          return sdId === subDeptId;
        });
      });
      setSelectedSubDepartments(validSubDepartmentIds);

      await filterUsersBySelections(selectedBranches, departmentIds, validSubDepartmentIds);
    } else {
      if (selectedBranches.length > 0) {
        const filtered = departments.filter(dept => selectedBranches.includes(dept.branch_id));
        const filteredDeptIds = filtered.map(d => d.id);
        const subs = subDepartments.filter(sd => {
          const sdDeptId = sd.department_id || sd.DepartmentId || sd.departmentId;
          return filteredDeptIds.includes(sdDeptId);
        });
        setFilteredSubDepartments(subs);

        const validSubDepartmentIds = selectedSubDepartments.filter(subDeptId => {
          return subs.some(sd => {
            const sdId = sd.id || sd.sub_department_id || sd.subDepartmentId;
            return sdId === subDeptId;
          });
        });
        setSelectedSubDepartments(validSubDepartmentIds);

        await filterUsersBySelections(selectedBranches, [], validSubDepartmentIds);
      } else {
        setFilteredSubDepartments(subDepartments);
        setFilteredUsers(availableUsers);
        setSelectedSubDepartments([]);
      }
    }
  };

  // Handle sub-department change - filter users
  const handleSubDepartmentChange = async (subDepartmentIds, selectedOptions) => {
    setSelectedSubDepartments(subDepartmentIds);
    await filterUsersBySelections(selectedBranches, selectedDepartments, subDepartmentIds);
  };

  // Fetch users by filters
  const fetchUsersByFilters = async (branchIds, departmentIds, subDepartmentIds) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;

      if (!corporateId) return [];

      const params = new URLSearchParams();
      params.append('corporateId', parseInt(corporateId));

      if (branchIds && branchIds.length > 0) {
        params.append('branch', branchIds.join(','));
      }

      if (departmentIds && departmentIds.length > 0) {
        params.append('department', departmentIds.join(','));
      }

      if (subDepartmentIds && subDepartmentIds.length > 0) {
        params.append('sub_department', subDepartmentIds.join(','));
      }

      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];

      const filteredUsers = users.filter(user => user.id !== userData.id);

      return filteredUsers;
    } catch (error) {
      console.error('Error fetching users by filters:', error);
      return [];
    }
  };

  // Filter users based on selected branches, departments, and sub-departments
  const filterUsersBySelections = async (branchIds, departmentIds, subDepartmentIds) => {
    if (branchIds.length > 0 || departmentIds.length > 0 || subDepartmentIds.length > 0) {
      const users = await fetchUsersByFilters(branchIds, departmentIds, subDepartmentIds);
      setFilteredUsers(users);

      if (selectedUsers.length > 0) {
        const validUserIds = selectedUsers.filter(userId =>
          users.some(user => {
            const userIdMatch = user.id === userId || user.CorporateUserId === userId;
            return userIdMatch;
          })
        );
        if (validUserIds.length !== selectedUsers.length) {
          setSelectedUsers(validUserIds);
        }
      }
    } else {
      setFilteredUsers(availableUsers);
    }
  };

  // Handle user change
  const handleUserChange = (userIds, selectedOptions) => {
    setSelectedUsers(userIds);
  };

  // Handle share document (internal)
  const handleShareDocument = async () => {
    if (!documentData || !documentData.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setSharing(true);

      const payload = buildDocumentSharePayload(
        selectedBranches,
        selectedDepartments,
        selectedSubDepartments,
        selectedUsers
      );

      const response = await axiosInstance.put(`/documents/${documentData.id}/share`, payload);

      if (response.status === 200) {
        notifySuccess('Document shared successfully');
        setShowShareModal(false);
        // Reset form
        setSelectedBranches([]);
        setSelectedDepartments([]);
        setSelectedSubDepartments([]);
        setSelectedUsers([]);
        setFilteredDepartments(departments);
        setFilteredSubDepartments(subDepartments);
        setFilteredUsers(availableUsers);
        // Refresh document details
        await fetchDocumentDetails();
      }
    } catch (error) {
      console.error('Error sharing document:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to share document';
      notifyError(errorMessage);
    } finally {
      setSharing(false);
    }
  };

  // Handle email share document
  const handleEmailShareDocument = async (payload) => {
    if (!documentData || !documentData.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setSharing(true);

      const requestPayload = {
        fileId: documentData.id,
        email: payload.email,
        permission: 'read',
        expiryOption: payload.expiryOption || 'never'
      };

      const response = await axiosInstance.post('/api/share', requestPayload);

      if (response.status === 200 || response.status === 201) {
        notifySuccess('Document shared via email successfully');
        setShowShareModal(false);
        // Refresh document details
        await fetchDocumentDetails();
      }
    } catch (error) {
      console.error('Error sharing document via email:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to share document via email';
      notifyError(errorMessage);
    } finally {
      setSharing(false);
    }
  };

  const applyLoadedShares = () => {
    const shares = documentShares?.shares;
    if (!shares) return;
    const branchIds = (shares.branches || []).map((b) => b.id || b.branch_id).filter(Boolean);
    const departmentIds = (shares.departments || []).map((d) => d.id || d.department_id).filter(Boolean);
    const subDepartmentIds = (shares.sub_departments || []).map((sd) => sd.id || sd.sub_department_id).filter(Boolean);
    setSelectedBranches(branchIds);
    setSelectedDepartments(departmentIds);
    setSelectedSubDepartments(subDepartmentIds);
    if ((shares.users || []).length) rememberShareUsers(shares.users);
    else setSelectedUsers([]);
  };

  const openShareModal = () => {
    applyLoadedShares();
    setShowShareModal(true);
  };

  // Reset share form
  const resetShareForm = () => {
    setSelectedBranches([]);
    setSelectedDepartments([]);
    setSelectedSubDepartments([]);
    setSelectedUsers([]);
    setFilteredDepartments(departments);
    setFilteredSubDepartments(subDepartments);
    setFilteredUsers(availableUsers);
  };

  const handleDownload = async () => {
    if (!documentData || !documentData.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setDownloading(true);

      const response = await axiosInstance.get(`/documents/${documentData.id}/download`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/octet-stream'
        }
      });

      if (response.status === 200) {
        let fileName = documentData.name || documentData.file_name || 'document';

        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
          }
        }

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        notifySuccess('File downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading file:', error);

      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          notifyError(errorJson.message || 'Failed to download file');
        } catch (parseError) {
          notifyError('Failed to download file');
        }
      } else {
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to download file';
        notifyError(errorMessage);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleAddFavorite = async () => {
    if (!documentData || !documentData.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setLoading(true);

      const response = await axiosInstance.put(`/documents/${documentData.id}/favorite`);

      if (response.status === 200 || response.status === 204) {
        const isFavorited = response.data?.data?.is_favorite ?? true;
        
        // Update documentData immediately for responsive UI
        setDocumentData(prev => ({
          ...prev,
          is_favorite: isFavorited
        }));
        
        if (isFavorited) {
          notifySuccess('Document added to favorites');
        } else {
          notifySuccess('Document removed from favorites');
        }
        // Optionally refresh document details to get updated favorite status
        await fetchDocumentDetails();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to toggle favorite';
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!documentData) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/documents/${documentData.id}`);

      if (response.status === 200 || response.status === 204) {
        const responseData = response.data;

        if (responseData && responseData.success === false) {
          notifyError(responseData.message || 'Failed to delete document');
        } else {
          notifySuccess('Document deleted successfully');
          setShowDeleteModal(false);
          setTimeout(() => {
            navigate('/documents');
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to delete document';
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateBack = () => {
    const listQuery = pickQuerySuffix(location.search, DOCUMENT_LIST_QUERY_KEYS);
    const from = location.state?.from;
    // List places files by category_id (folder); prefer that for Back fallback
    const folderId =
      documentData?.category_id ||
      documentData?.document_path_id ||
      null;
    const folderFallback = folderId
      ? `/documents/folder/${folderId}`
      : '/documents/mydocuments';
    const listPath =
      typeof from === 'string' && from.startsWith('/documents/')
        ? from
        : folderFallback;
    navigateBackSafely(navigate, withListQuery(listPath, listQuery));
  };

  // Prefill version form fields when modal opens
  useEffect(() => {
    if (showUploadVersionModal && documentData) {
      setVersionDocumentTitle(documentData.document_title || '');
      setVersionRegisteredTo(documentData.registered_to || '');
      setVersionReferenceNumber(documentData.reference_number || '');
      setVersionIssueDate(documentData.issue_date || '');
      setVersionExpiryDate(documentData.expiry_date || '');
      setVersionRemindBefore(documentData.remind_before || '');
      setVersionIssuingAuthority(documentData.issuing_authority || '');
      setVersionOtherComments(documentData.other_comments || '');
    }
  }, [showUploadVersionModal, documentData]);

  const documentDetailToolbar = useMemo(() => (
    <>
      <div className="page-toolbar-bar__start">
        <button
          type="button"
          className="page-toolbar-back-btn"
          onClick={handleNavigateBack}
          title="Back"
          aria-label="Back"
        >
          <FiArrowLeft />
          Back
        </button>
      </div>
      {documentData ? (
        <div className="page-toolbar-bar__end">
          {canDownloadDocuments ? (
          <button
            className="header-toolbar-btn"
            onClick={handleDownload}
            disabled={downloading}
            type="button"
            title={downloading ? 'Downloading...' : 'Download'}
            aria-label={downloading ? 'Downloading...' : 'Download'}
          >
            <FiDownload />
          </button>
          ) : null}
          {canShareDocuments ? (
          <button
            className="header-toolbar-btn"
            onClick={openShareModal}
            type="button"
            title="Share"
            aria-label="Share"
          >
            <FiShare2 />
          </button>
          ) : null}
          <button
            type="button"
            className={`header-toolbar-btn${infoOpen ? ' is-active' : ''}`}
            onClick={() => setInfoOpen((open) => !open)}
            aria-expanded={infoOpen}
            aria-controls="document-info-panel"
            title="Info"
            aria-label="Info"
          >
            <i className="fas fa-info-circle" aria-hidden />
          </button>
          {canEditDocuments ? (
          <button
            className="header-toolbar-btn"
            onClick={() => navigate(`/documents/edit/${documentId}`)}
            type="button"
            title="Edit Document"
            aria-label="Edit Document"
          >
            <FiEdit2 />
          </button>
          ) : null}
          <button
            className="header-toolbar-btn"
            onClick={handleAddFavorite}
            type="button"
            disabled={loading}
            title={loading ? (documentData?.is_favorite ? 'Removing...' : 'Adding...') : (documentData?.is_favorite ? 'Remove from Favorites' : 'Add to Favorites')}
            aria-label={documentData?.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
            style={documentData?.is_favorite ? { color: '#ffc107' } : undefined}
          >
            {documentData?.is_favorite ? <FaStar /> : <FiStar />}
          </button>
          {canDeleteDocuments ? (
          <button
            className="header-toolbar-btn"
            onClick={() => setShowDeleteModal(true)}
            type="button"
            style={{ color: '#dc3545' }}
            title="Delete"
            aria-label="Delete"
          >
            <FiTrash2 />
          </button>
          ) : null}
          {canEditDocuments ? (
          <button
            className="header-toolbar-btn seg-btn--wide"
            onClick={() => {
              const params = new URLSearchParams(location.search || '');
              params.set('mode', 'version');
              params.set('documentId', String(documentData.id));
              navigate(`/documents/upload?${params.toString()}`, {
                state: { from: location.pathname },
              });
            }}
            type="button"
            title="Upload New Version"
            aria-label="Upload New Version"
          >
            <FiUpload />
          </button>
          ) : null}
        </div>
      ) : null}
    </>
  ), [documentData, documentShares, downloading, loading, documentId, location.search, location.pathname, navigate, infoOpen, canEditDocuments, canDeleteDocuments, canShareDocuments, canDownloadDocuments]);

  usePageToolbar(documentDetailToolbar, [documentDetailToolbar]);

  // Show loading while checking access (after all hooks)
  if (accessLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render if no access (will redirect)
  if (!hasAccess) {
    return null;
  }

  // Handle upload new version
  const handleUploadVersion = async () => {
    if (!uploadVersionFile || !documentData || !documentData.id) {
      notifyError('Please select a file to upload');
      return;
    }

    if (!versionDocumentTitle.trim()) {
      notifyError('Document title is required');
      return;
    }

    try {
      setUploadingVersion(true);

      const formData = new FormData();
      formData.append('file', uploadVersionFile);
      formData.append('document_title', versionDocumentTitle);

      // Optional document fields
      if (versionRegisteredTo) {
        formData.append('registered_to', versionRegisteredTo);
      }
      if (versionReferenceNumber) {
        formData.append('reference_number', versionReferenceNumber);
      }
      if (versionIssueDate) {
        formData.append('issue_date', versionIssueDate);
      }
      if (versionExpiryDate) {
        formData.append('expiry_date', versionExpiryDate);
      }
      if (versionRemindBefore) {
        formData.append('remind_before', versionRemindBefore);
      }
      if (versionIssuingAuthority) {
        formData.append('issuing_authority', versionIssuingAuthority);
      }
      if (versionOtherComments) {
        formData.append('other_comments', versionOtherComments);
      }

      const response = await axiosInstance.post(`/documents/${documentData.id}/upload-version`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200 || response.status === 201) {
        notifySuccess('New version uploaded successfully');
        setShowUploadVersionModal(false);
        setUploadVersionFile(null);
        // Reset form fields
        setVersionDocumentTitle('');
        setVersionRegisteredTo('');
        setVersionReferenceNumber('');
        setVersionIssueDate('');
        setVersionExpiryDate('');
        setVersionRemindBefore('');
        setVersionIssuingAuthority('');
        setVersionOtherComments('');
        // Refresh document details to get updated version info
        await fetchDocumentDetails();
      }
    } catch (error) {
      console.error('Error uploading new version:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to upload new version';
      notifyError(errorMessage);
    } finally {
      setUploadingVersion(false);
    }
  };

  const handleDownloadVersion = async (versionNumber) => {
    if (!documentData || !documentData.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setDownloading(true);

      const response = await axiosInstance.get(
        `/documents/${documentData.id}/download?version=${versionNumber}`,
        {
          responseType: 'blob',
          headers: {
            'Accept': 'application/octet-stream'
          }
        }
      );

      if (response.status === 200) {
        // Axios returns headers in lowercase, so check both cases
        const headers = response.headers;
        let fileName = headers['x-file-name'] || headers['X-File-Name'];

        // If custom header not available, try Content-Disposition header
        if (!fileName) {
          const contentDisposition = headers['content-disposition'] || headers['Content-Disposition'];
          if (contentDisposition) {
            // Try to extract from filename* (UTF-8 encoded) first
            const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
            if (utf8Match) {
              fileName = decodeURIComponent(utf8Match[1]);
            } else {
              // Fall back to regular filename
              const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (fileNameMatch && fileNameMatch[1]) {
                fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
              }
            }
          }
        } else {
          // Decode the custom header if it's encoded
          try {
            fileName = decodeURIComponent(fileName);
          } catch (e) {
            // If decoding fails, use as-is
            console.warn('Failed to decode filename:', e);
          }
        }

        // Fallback to document name if still no filename
        if (!fileName) {
          const fileType = headers['x-file-type'] || headers['X-File-Type'] || 'file';
          fileName = documentData.name || documentData.file_name || `document_v${versionNumber}.${fileType}`;
        }

        // Get content type from headers (check both cases)
        const contentType = headers['x-content-type'] ||
          headers['X-Content-Type'] ||
          headers['content-type'] ||
          headers['Content-Type'] ||
          'application/octet-stream';

        // Create blob with proper MIME type
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();

        // Cleanup
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);

        notifySuccess(`Version ${versionNumber} downloaded successfully`);
      }
    } catch (error) {
      console.error('Error downloading version:', error);

      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          notifyError(errorJson.message || 'Failed to download version');
        } catch (parseError) {
          notifyError('Failed to download version');
        }
      } else {
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to download version';
        notifyError(errorMessage);
      }
    } finally {
      setDownloading(false);
    }
  };

  const getClassificationIcon = (classificationName) => {
    if (!classificationName) return null;

    const normalizedName = classificationName.toLowerCase().trim();

    // Map classification names to icon types
    const classificationMap = {
      'contracts': 'word',
      'contract': 'word',
      'legal': 'word',
      'reports': 'pdf',
      'report': 'pdf',
      'invoices': 'excel',
      'invoice': 'excel',
      'financial': 'excel',
      'finance': 'excel',
      'hr documents': 'word',
      'hr': 'word',
      'human resources': 'word',
      'project documents': 'word',
      'project': 'word',
      'general': 'file',
      'images': 'image',
      'image': 'image',
      'photos': 'image',
      'photo': 'image',
      'presentations': 'powerpoint',
      'presentation': 'powerpoint',
      'slides': 'powerpoint',
      'spreadsheets': 'excel',
      'spreadsheet': 'excel',
      'documents': 'word',
      'document': 'word',
      'archives': 'archive',
      'archive': 'archive',
      'zip': 'archive'
    };

    return classificationMap[normalizedName] || null;
  };

  const getFileIcon = (fileType, classificationName = null) => {

    // Fallback to file type
    if (!fileType) return 'file';

    let normalizedType = fileType.toLowerCase();
    if (normalizedType[0] === '.') {
      normalizedType = normalizedType.substring(1);
    }

    const iconMap = {
      pdf: 'pdf',
      doc: 'word',
      docx: 'word',
      xls: 'excel',
      xlsx: 'excel',
      ppt: 'powerpoint',
      pptx: 'powerpoint',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      gif: 'image',
      txt: 'text',
      zip: 'archive',
      rar: 'archive'
    };

    return iconMap[normalizedType] || 'file';
  };

  const getFileIconComponent = (fileType, size = 64, classificationName = null) => {
    const iconType = getFileIcon(fileType, classificationName);

    switch (iconType) {
      case 'pdf':
        return <FaFilePdf size={size} style={{ color: '#d93025' }} />;
      case 'word':
        return <FaFileWord size={size} style={{ color: '#2b579a' }} />;
      case 'excel':
        return <FaFileExcel size={size} style={{ color: '#1d6f42' }} />;
      case 'powerpoint':
        return <FaFilePowerpoint size={size} style={{ color: '#d04423' }} />;
      case 'image':
        return <FaFileImage size={size} style={{ color: '#34a853' }} />;
      case 'archive':
        return <FaFileArchive size={size} style={{ color: '#ea4335' }} />;
      case 'text':
        return <FaFileAlt size={size} style={{ color: '#6c757d' }} />;
      default:
        return <FiFile size={size} style={{ color: '#C42126' }} />;
    }
  };

  if (loading && !documentData) {
    return (
      <React.Fragment>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3">
              <h5>Loading document details...</h5>
              <p className="text-muted">Please wait while we fetch the document information.</p>
            </div>
          </div>
        </div>
        <ToastContainer />
      </React.Fragment>
    );
  }

  if (!documentData) {
    return (
      <React.Fragment>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="text-center">
            <h5>Document not found</h5>
            <p className="text-muted">The document you're looking for doesn't exist or you don't have permission to view it.</p>
            <button className="btn btn-primary mt-3" onClick={handleNavigateBack}>
              Go Back
            </button>
          </div>
        </div>
        <ToastContainer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="row forum-detail-row">
        <ToastContainer />
        <div className="col-xl-12 col-xxl-12">
          <div className="card heightcard forum-detail-card">
            <div className="card-header details-card-header forum-section-header forum-title-block">
              <div className="forum-title-block__info">
                <h5 className="text-base sm:text-lg font-semibold mb-0 forum-title-row__title">
                  <span className="forum-title-row__title-text d-inline-flex align-items-center gap-2">
                    <span className="document-icon-header">
                      {getFileIconComponent(documentData.file_type, 28, documentData.category_name)}
                    </span>
                    {documentData.document_title || documentData.name || documentData.file_name || documentData.name}
                  </span>
                </h5>
                <div className="forum-title-block__labels">
                  <span className="forum-title-meta-label">
                    Type: {documentData.file_type?.toUpperCase() || 'Unknown'}
                  </span>
                  <span className="forum-title-meta-label">
                    Version: {documentData.version || 1}
                  </span>
                  {documentData.size && (
                    <span className="forum-title-meta-label">
                      Size: {documentData.size}
                    </span>
                  )}
                  {classificationPath && Array.isArray(classificationPath) && classificationPath.length > 0 && (
                    <span className="forum-title-meta-label">
                      Uploaded under:{' '}
                      {classificationPath.map((category, index) => (
                        <span key={category.id}>
                          {index > 0 ? ' / ' : ''}
                          <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            style={{
                              fontSize: 'inherit',
                              lineHeight: 'inherit',
                              color: index === classificationPath.length - 1 ? '#212529' : '#6c757d',
                              fontWeight: index === classificationPath.length - 1 ? 600 : 500,
                              textDecoration: 'underline',
                            }}
                            onClick={() => navigate(`/documents/folder/${category.id}`)}
                          >
                            {category.name}
                          </button>
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="card-body forum-detail-card__body">
              <section>
                <div className="row">
                  {/* Document Details - Reference Number, Expiry Date, Registered To, Issue Date, Issuing Authority, Other Comments */}
                  {(documentData.reference_number || documentData.expiry_date || documentData.registered_to || documentData.issue_date || documentData.issuing_authority || documentData.other_comments) && (
                    <div className="col-lg-12 mb-4">
                      <div className="row">
                        {/* Reference Number */}
                        {documentData.reference_number && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Reference Number</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {documentData.reference_number}
                            </div>
                          </div>
                        )}

                        {/* Expiry Date */}
                        {documentData.expiry_date && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Expiry Date</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {formatDateToDDMMMYYYY(documentData.expiry_date)}
                            </div>
                          </div>
                        )}

                        {/* Registered To */}
                        {documentData.registered_to && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Registered To</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {documentData.registered_to}
                            </div>
                          </div>
                        )}

                        {/* Issue Date */}
                        {documentData.issue_date && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Issue Date</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {formatDateToDDMMMYYYY(documentData.issue_date)}
                            </div>
                          </div>
                        )}

                        {/* Issuing Authority */}
                        {documentData.issuing_authority && (
                          <div className="col-md-4 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Issuing Authority</label>
                            <div style={{
                              padding: '0.75rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              color: '#212529',
                              wordBreak: 'break-word',
                              transition: 'all 0.2s ease',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {documentData.issuing_authority}
                            </div>
                          </div>
                        )}

                        {/* Other Comments */}
                        {documentData.other_comments && (
                          <div className="col-md-12 mb-3">
                            <label className="text-label" style={{
                              marginBottom: 0,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: '#495057',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Other Comments</label>
                            <div style={{
                              padding: '1rem 0',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              fontWeight: '400',
                              color: '#495057',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                              transition: 'all 0.2s ease',
                              lineHeight: '1.6',
                              minHeight: '80px'
                            }}>
                              {documentData.other_comments}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Document Description */}
                  {documentData.description && (
                    <div className="col-lg-12 mb-4">
                      <label className="text-label">Description</label>
                      <div className="document-description p-3">
                        <p>{documentData.description}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>

          {/* Document Preview Section - Separate section */}
          {previewUrl && (
            <div className="card heightcard">
              <div className="card-header details-card-header forum-section-header">
                <h5 className="text-base sm:text-lg font-semibold mb-0">Document Preview</h5>
              </div>
              <div className="card-body">
                <div className="document-preview-container" style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '1rem',
                  minHeight: '500px',
                  maxHeight: '600px',
                  backgroundColor: '#f8f9fa',
                  overflow: (() => {
                    const fileType = documentData?.file_type?.replace(/^\./, '').toLowerCase() ||
                      (documentData?.name || documentData?.file_name || '').split('.').pop()?.toLowerCase() || '';
                    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
                    // Hide overflow for images to prevent scrollbars, allow scroll for other file types
                    return imageTypes.includes(fileType) ? 'hidden' : 'auto';
                  })(),
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {previewError ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                      <div className="text-center">
                        <FiFile size={48} className="text-muted mb-3" />
                        <p className="text-muted">{previewError}</p>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      width: '100%',
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      overflow: (() => {
                        // Extract file type, handling both MIME types (image/png) and extensions (.png)
                        let fileType = '';
                        const rawFileType = documentData?.file_type || '';
                        const fileName = documentData?.name || documentData?.file_name || '';
                        
                        // Check if it's a MIME type (contains '/')
                        if (rawFileType.includes('/')) {
                          const normalizedMime = rawFileType.toLowerCase().trim();
                          fileType = normalizedMime.split('/').pop() || '';
                        } else {
                          fileType = rawFileType.replace(/^\./, '').toLowerCase();
                        }
                        
                        // Fallback to filename extension
                        if (!fileType && fileName) {
                          fileType = fileName.split('.').pop()?.toLowerCase() || '';
                        }
                        
                        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
                        // Hide overflow for images to prevent scrollbars
                        return imageTypes.includes(fileType) ? 'hidden' : 'auto';
                      })()
                    }}>
                      {(() => {
                        // Extract file type, handling both MIME types (image/png) and extensions (.png)
                        let fileType = '';
                        const rawFileType = documentData.file_type || '';
                        const fileName = documentData.name || documentData.file_name || '';
                        
                        // Check if it's a MIME type (contains '/')
                        if (rawFileType.includes('/')) {
                          // Extract extension from MIME type (e.g., "image/png" -> "png")
                          const mimeToExt = {
                            'image/jpeg': 'jpg',
                            'image/jpg': 'jpg',
                            'image/png': 'png',
                            'image/gif': 'gif',
                            'image/bmp': 'bmp',
                            'image/webp': 'webp',
                            'application/pdf': 'pdf',
                            'application/msword': 'doc',
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
                            'application/vnd.ms-excel': 'xls',
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
                            'application/vnd.ms-powerpoint': 'ppt',
                            'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
                            'text/plain': 'txt',
                            'text/csv': 'csv'
                          };
                          const normalizedMime = rawFileType.toLowerCase().trim();
                          fileType = mimeToExt[normalizedMime] || normalizedMime.split('/').pop() || '';
                        } else {
                          // It's an extension, just clean it up
                          fileType = rawFileType.replace(/^\./, '').toLowerCase();
                        }
                        
                        // Fallback to filename extension if file_type is not available or not recognized
                        if (!fileType && fileName) {
                          fileType = fileName.split('.').pop()?.toLowerCase() || '';
                        }
                        
                        console.log('Rendering preview with:', { fileType, rawFileType, filePath: previewUrl });

                        // Excel files cannot be previewed - show download option instead
                        if (fileType === 'xls' || fileType === 'xlsx' || fileType === 'excel') {
                          return (
                            <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                              <FaFileExcel size={64} className="text-muted mb-3" style={{ color: '#1d6f42' }} />
                              <p className="text-muted mb-3">Excel files cannot be previewed in the browser.</p>
                              <button
                                className="btn btn-primary"
                                onClick={handleDownload}
                                disabled={downloading}
                              >
                                <FiDownload className="me-2" />
                                {downloading ? 'Downloading...' : 'Download File'}
                              </button>
                            </div>
                          );
                        }

                        // Use iframe for PDFs as browsers handle them natively
                        if (fileType === 'pdf') {
                          return (
                            <iframe
                              src={`${previewUrl}#toolbar=0`}
                              style={{
                                width: '100%',
                                height: '100%',
                                minHeight: '500px',
                                border: 'none',
                                flex: 1
                              }}
                              title="PDF Preview"
                              onError={(e) => {
                                console.error('Iframe error:', e);
                                onError(e);
                              }}
                            />
                          );
                        }

                        // Handle common image types with an <img> so we can control sizing better
                        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
                        if (imageTypes.includes(fileType) && previewUrl) {
                          return (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden'
                              }}
                            >
                              <img
                                src={previewUrl}
                                alt={documentData.name || documentData.file_name || 'document'}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  borderRadius: '4px',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                                }}
                              />
                            </div>
                          );
                        }

                        // List of file types that FileViewer supports well
                        const supportedPreviewTypes = ['doc', 'docx', 'ppt', 'pptx', 'txt', 'csv'];

                        // Check if file type is supported, otherwise show download option
                        if (!supportedPreviewTypes.includes(fileType)) {
                          return (
                            <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '500px' }}>
                              <FiFile size={64} className="text-muted mb-3" />
                              <p className="text-muted mb-3">Preview not available for this file type.</p>
                              <button
                                className="btn btn-primary"
                                onClick={handleDownload}
                                disabled={downloading}
                              >
                                <FiDownload className="me-2" />
                                {downloading ? 'Downloading...' : 'Download File'}
                              </button>
                            </div>
                          );
                        }

                        // Use FileViewer for supported file types
                        return (
                          <FileViewer
                            fileType={fileType}
                            filePath={previewUrl}
                            onError={onError}
                            errorComponent={<div className="text-center p-4"><p className="text-muted">Unable to preview this file type</p></div>}
                            unsupportedComponent={<div className="text-center p-4"><p className="text-muted">File type not supported for preview</p></div>}
                          />
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Version Log Section - Below the main container */}
          <div className="card heightcard">
            <div className="card-header details-card-header forum-section-header">
              <div className="d-flex align-items-center justify-content-between" style={{ width: '100%' }}>
                <h5 className="text-base sm:text-lg font-semibold mb-0">Version History</h5>
                <span className="badge bg-primary">{versionHistory.length || 1} version(s)</span>
              </div>
            </div>
            <div className="card-body">
              <div className="version-log-container" style={{
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {versionHistory && versionHistory.length > 0 ? (
                  <div className="version-list">
                    {versionHistory
                      .sort((a, b) => {
                        const versionA = a.version || a.version_number || 0;
                        const versionB = b.version || b.version_number || 0;
                        return versionB - versionA; // Sort descending (newest first)
                      })
                      .map((version, index) => (
                        <div
                          key={version.id || index}
                          className="version-item mb-3 pb-3"
                          style={{
                            borderBottom: index < versionHistory.length - 1 ? '1px solid #e9ecef' : 'none'
                          }}
                        >
                          <div className="d-flex align-items-start gap-3">
                            <div className="version-icon" style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background: index === 0 ? '#667eea' : '#e9ecef',
                              color: index === 0 ? 'white' : '#6c757d',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1rem',
                              fontWeight: 'bold',
                              flexShrink: 0
                            }}>
                              {version.version || version.version_number || index + 1}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="fw-semibold" style={{ fontSize: '1rem' }}>
                                  Version {version.version || version.version_number || index + 1}
                                  {index === 0 && (
                                    <span className="badge bg-success ms-2" style={{ fontSize: '0.75rem' }}>Current</span>
                                  )}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                  {version.file_size && (
                                    <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                                      {(parseFloat(version.file_size) / 1024 / 1024).toFixed(2)} MB
                                    </span>
                                  )}
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleDownloadVersion(version.version || version.version_number || index + 1)}
                                    disabled={downloading}
                                    title={`Download Version ${version.version || version.version_number || index + 1}`}
                                    style={{
                                      padding: '0.25rem 0.5rem',
                                      fontSize: '0.75rem',
                                      border: '1px solid #667eea',
                                      color: '#667eea'
                                    }}
                                  >
                                    <FiDownload size={14} />
                                  </button>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <FiClock size={16} className="text-muted" />
                                <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                                  {version.created_at || version.uploaded_at
                                    ? formatDateTimeToDDMMMYYYY(version.created_at || version.uploaded_at)
                                    : 'Unknown date'}
                                </span>
                              </div>
                              {version.uploaded_by_user ? (
                                <div className="text-muted mb-1" style={{ fontSize: '0.875rem' }}>
                                  Uploaded by: <strong>{version.uploaded_by_user.full_name || version.created_by_name}</strong>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <FiClock size={48} className="mb-3" style={{ opacity: 0.5 }} />
                    <p className="mb-2">No version history available</p>
                    <p className="mb-0" style={{ fontSize: '0.875rem' }}>
                      Current version: <strong>{documentData.version || 1}</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {infoOpen && (
        <button
          type="button"
          className="forum-info-backdrop"
          onClick={() => setInfoOpen(false)}
          aria-label="Close info panel"
        />
      )}

      <aside
        id="document-info-panel"
        className={`forum-info-drawer${infoOpen ? ' forum-info-drawer--open' : ''}`}
        aria-hidden={!infoOpen}
      >
        <div className="forum-info-drawer-header">
          <h6 className="forum-info-drawer-title mb-0">Info</h6>
          <button
            type="button"
            className="forum-info-drawer-close"
            onClick={() => setInfoOpen(false)}
            aria-label="Close info panel"
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>
        <div className="forum-info-drawer-body">
          <div className="forum-info-field">
            <p className="forum-info-field-label">File Name</p>
            <p className="forum-info-field-value">{documentData.name || documentData.file_name || '—'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">File Size</p>
            <p className="forum-info-field-value">{documentData.size || '—'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">File Type</p>
            <p className="forum-info-field-value">{documentData.file_type?.toUpperCase() || 'Unknown'}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Version</p>
            <p className="forum-info-field-value">{documentData.version || 1}</p>
          </div>
          {documentData.folder_name && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Folder</p>
              <p className="forum-info-field-value">{documentData.folder_name}</p>
            </div>
          )}
          <div className="forum-info-field">
            <p className="forum-info-field-label">Created on</p>
            <p className="forum-info-field-value">{formatDateTimeToDDMMMYYYY(documentData.created_at)}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Last Modified</p>
            <p className="forum-info-field-value">{formatDateTimeToDDMMMYYYY(documentData.updated_at || documentData.created_at)}</p>
          </div>
          <div className="forum-info-field">
            <p className="forum-info-field-label">Privacy</p>
            <p className="forum-info-field-value">{documentData.is_private ? 'Private' : 'Public'}</p>
          </div>
          {documentData.created_by_name && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Uploaded by</p>
              <p className="forum-info-field-value">{documentData.created_by_name}</p>
            </div>
          )}
          {documentData.remind_before && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Remind Before</p>
              <p className="forum-info-field-value">{documentData.remind_before}</p>
            </div>
          )}
          {documentData.branches && documentData.branches.length > 0 && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Branches</p>
              <div className="user-chip-list">
                {documentData.branches.map((branch, index) => (
                  <div className="user-chip" key={index}>
                    <span className="user-chip-avatar">
                      <i className="fa fa-sitemap"></i>
                    </span>
                    {branch.name || branch.branch_name || 'Unknown Branch'}
                  </div>
                ))}
              </div>
            </div>
          )}
          {documentData.departments && documentData.departments.length > 0 && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Departments</p>
              <div className="user-chip-list">
                {documentData.departments.map((dept, index) => (
                  <div className="user-chip" key={index}>
                    <span className="user-chip-avatar">
                      <i className="fa fa-building"></i>
                    </span>
                    {dept.name || dept.department_name || 'Unknown Department'}
                  </div>
                ))}
              </div>
            </div>
          )}
          {documentData.sub_departments && documentData.sub_departments.length > 0 && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Sub Departments</p>
              <div className="user-chip-list">
                {documentData.sub_departments.map((subDept, index) => (
                  <div className="user-chip" key={index}>
                    <span className="user-chip-avatar">
                      <i className="fa fa-building"></i>
                    </span>
                    {subDept.name || subDept.sub_department_name || 'Unknown Sub Department'}
                  </div>
                ))}
              </div>
            </div>
          )}
          {documentData?.is_linked_to_staff && (documentData?.linked_staff || documentData?.staff_id) && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Linked to Staff</p>
              <p className="forum-info-field-value">
                {documentData.linked_staff ? (
                  <a
                    href={`/staff/staffDetails/${documentData.linked_staff.id || documentData.staff_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                  >
                    {documentData.linked_staff.employee_code ? `${documentData.linked_staff.employee_code} - ` : ''}
                    {[documentData.linked_staff.first_name, documentData.linked_staff.middle_name, documentData.linked_staff.last_name].filter(Boolean).join(' ') ||
                      documentData.linked_staff.name ||
                      documentData.linked_staff.full_name ||
                      'Staff'}
                    <i className="fas fa-external-link-alt ms-2" style={{ fontSize: '0.75rem' }}></i>
                  </a>
                ) : (
                  <a
                    href={`/staff/staffDetails/${documentData.staff_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                  >
                    View Staff (ID: {documentData.staff_id})
                    <i className="fas fa-external-link-alt ms-2" style={{ fontSize: '0.75rem' }}></i>
                  </a>
                )}
              </p>
            </div>
          )}
          {documentData?.is_linked_to_vehicle && (documentData?.linked_vehicle || documentData?.vehicle_id) && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Linked to Vehicle</p>
              <p className="forum-info-field-value">
                {documentData.linked_vehicle ? (
                  <a
                    href={`/vehicle/vehicleDetails/${documentData.linked_vehicle.id || documentData.vehicle_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                  >
                    {documentData.linked_vehicle.registration_number ||
                      documentData.linked_vehicle.name ||
                      `Vehicle #${documentData.linked_vehicle.id || documentData.vehicle_id}`}
                    <i className="fas fa-external-link-alt ms-2" style={{ fontSize: '0.75rem' }}></i>
                  </a>
                ) : (
                  <a
                    href={`/vehicle/vehicleDetails/${documentData.vehicle_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                  >
                    View Vehicle (ID: {documentData.vehicle_id})
                    <i className="fas fa-external-link-alt ms-2" style={{ fontSize: '0.75rem' }}></i>
                  </a>
                )}
              </p>
            </div>
          )}

          {documentShares && (
            <div className="forum-info-field">
              <p className="forum-info-field-label">Document Shares</p>
              {loadingShares ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : documentShares?.is_shared && documentShares?.shares ? (
                <div className="document-shares-list">
                  {documentShares.shares.branches && documentShares.shares.branches.length > 0 && (
                    <div className="mb-3">
                      <p className="forum-info-field-label mb-2">Shared Branches</p>
                      {documentShares.shares.branches.map((branch) => (
                        <div key={branch.id} className="d-flex align-items-center justify-content-between mb-2 p-2" style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div className="d-flex align-items-center">
                            <i className="fa fa-sitemap me-2" style={{ color: '#6c757d', fontSize: '0.875rem' }}></i>
                            <span style={{ fontSize: '0.875rem' }}>{branch.name}</span>
                          </div>
                          {isUploader() && (
                          <button
                            className="btn btn-sm btn-link p-0"
                            onClick={() => handleRevokeShareClick('branch', branch.id, branch.name)}
                            disabled={revokingShare}
                            title="Revoke share"
                            style={{ color: '#dc3545', padding: '0.25rem', minWidth: 'auto', fontSize: '0.875rem' }}
                          >
                            <FiX size={16} />
                          </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {documentShares.shares.departments && documentShares.shares.departments.length > 0 && (
                    <div className="mb-3">
                      <p className="forum-info-field-label mb-2">Shared Departments</p>
                      {documentShares.shares.departments.map((dept) => (
                        <div key={dept.id} className="d-flex align-items-center justify-content-between mb-2 p-2" style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div className="d-flex align-items-center">
                            <i className="fa fa-building me-2" style={{ color: '#6c757d', fontSize: '0.875rem' }}></i>
                            <span style={{ fontSize: '0.875rem' }}>{dept.name}</span>
                          </div>
                          {isUploader() && (
                          <button
                            className="btn btn-sm btn-link p-0"
                            onClick={() => handleRevokeShareClick('department', dept.id, dept.name)}
                            disabled={revokingShare}
                            title="Revoke share"
                            style={{ color: '#dc3545', padding: '0.25rem', minWidth: 'auto', fontSize: '0.875rem' }}
                          >
                            <FiX size={16} />
                          </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {documentShares.shares.sub_departments && documentShares.shares.sub_departments.length > 0 && (
                    <div className="mb-3">
                      <p className="forum-info-field-label mb-2">Shared Sub Departments</p>
                      {documentShares.shares.sub_departments.map((subDept) => (
                        <div key={subDept.id} className="d-flex align-items-center justify-content-between mb-2 p-2" style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div className="d-flex align-items-center">
                            <i className="fa fa-building me-2" style={{ color: '#6c757d', fontSize: '0.875rem' }}></i>
                            <span style={{ fontSize: '0.875rem' }}>{subDept.name}</span>
                          </div>
                          {isUploader() && (
                          <button
                            className="btn btn-sm btn-link p-0"
                            onClick={() => handleRevokeShareClick('sub_department', subDept.id, subDept.name)}
                            disabled={revokingShare}
                            title="Revoke share"
                            style={{ color: '#dc3545', padding: '0.25rem', minWidth: 'auto', fontSize: '0.875rem' }}
                          >
                            <FiX size={16} />
                          </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {documentShares.shares.users && documentShares.shares.users.length > 0 && (
                    <div className="mb-3">
                      <p className="forum-info-field-label mb-2">Shared Users</p>
                      {documentShares.shares.users.map((user) => (
                        <div key={user.id} className="d-flex align-items-center justify-content-between mb-2 p-2" style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                            <i className="fa fa-user me-2" style={{ color: '#6c757d', fontSize: '0.875rem' }}></i>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>{user.full_name}</div>
                              {user.email && (
                                <div style={{ fontSize: '0.75rem', color: '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {user.email}
                                </div>
                              )}
                            </div>
                          </div>
                          {isUploader() && (
                          <button
                            className="btn btn-sm btn-link p-0 ms-2"
                            onClick={() => handleRevokeShareClick('user', user.id, user.full_name || user.email)}
                            disabled={revokingShare}
                            title="Revoke share"
                            style={{ color: '#dc3545', padding: '0.25rem', minWidth: 'auto', fontSize: '0.875rem', flexShrink: 0 }}
                          >
                            <FiX size={16} />
                          </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {(!documentShares.shares.branches || documentShares.shares.branches.length === 0) &&
                    (!documentShares.shares.departments || documentShares.shares.departments.length === 0) &&
                    (!documentShares.shares.sub_departments || documentShares.shares.sub_departments.length === 0) &&
                    (!documentShares.shares.users || documentShares.shares.users.length === 0) && (
                      <p className="forum-info-field-value">No shares found</p>
                    )}
                  {isUploader() && ((documentShares.shares.branches && documentShares.shares.branches.length > 0) ||
                    (documentShares.shares.departments && documentShares.shares.departments.length > 0) ||
                    (documentShares.shares.sub_departments && documentShares.shares.sub_departments.length > 0) ||
                    (documentShares.shares.users && documentShares.shares.users.length > 0)) && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                      <Button
                        className="doc-form-cancel-btn"
                        onClick={() => setShowRevokeAllModal(true)}
                        disabled={revokingAllShares || revokingShare}
                        style={{ width: '100%', fontSize: '0.875rem' }}
                      >
                        {revokingAllShares ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Revoking All...
                          </>
                        ) : (
                          'Revoke All Shares'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="forum-info-field-value">Document is not shared</p>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => {
        setShowDeleteModal(false);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this document?</p>
          {documentData && (
            <div className="mt-3">
              <p className="mb-1"><strong>Document:</strong> {documentData?.document_title || documentData?.name || documentData?.file_name}</p>
            </div>
          )}
          <p className="mt-3 text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            className="doc-form-cancel-btn"
            onClick={() => {
              setShowDeleteModal(false);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            className="doc-form-submit-btn"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              'Delete Document'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Version Modal */}
      <Modal
        show={showUploadVersionModal}
        onHide={() => {
          setShowUploadVersionModal(false);
          setUploadVersionFile(null);
          // Reset form fields
          setVersionDocumentTitle('');
          setVersionRegisteredTo('');
          setVersionReferenceNumber('');
          setVersionIssueDate('');
          setVersionExpiryDate('');
          setVersionRemindBefore('');
          setVersionIssuingAuthority('');
          setVersionOtherComments('');
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload New Version</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            {/* File Upload */}
            <Form.Group className="mb-3">
              <Form.Label className="text-label">
                Select File <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setUploadVersionFile(file);
                  }
                }}
                accept="*/*"
              />
              {uploadVersionFile && (
                <div className="mt-2">
                  <small className="text-muted">
                    Selected: {uploadVersionFile.name} ({(uploadVersionFile.size / 1024 / 1024).toFixed(2)} MB)
                  </small>
                </div>
              )}
            </Form.Group>

            <Row>
              {/* Document Title */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-label">
                    Document Title <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={versionDocumentTitle}
                    onChange={(e) => setVersionDocumentTitle(e.target.value)}
                    placeholder="Enter document title"
                    required
                  />
                </Form.Group>
              </Col>

              {/* Document Registered To */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-label">
                    Document Registered to
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={versionRegisteredTo}
                    onChange={(e) => setVersionRegisteredTo(e.target.value)}
                    placeholder="Company name, person name, etc"
                  />
                </Form.Group>
              </Col>

              {/* Document Reference Number */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-label">
                    Document Reference Number
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={versionReferenceNumber}
                    onChange={(e) => setVersionReferenceNumber(e.target.value)}
                    placeholder="Trade License number, Passport number, etc"
                  />
                </Form.Group>
              </Col>

              {/* Document Issue Date */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-label">
                    Document Issue Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={versionIssueDate}
                    onChange={(e) => setVersionIssueDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Document Expiry Date */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-label">
                    Document Expiry Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={versionExpiryDate}
                    onChange={(e) => setVersionExpiryDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Remind me before - only show if expiry date is entered */}
              {versionExpiryDate && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-label">
                      Remind me before
                    </Form.Label>
                    <Select
                      options={[
                        { value: '1day', label: '1 day before' },
                        { value: '2days', label: '2 days before' },
                        { value: '1week', label: '1 week before' },
                        { value: '2weeks', label: '2 weeks before' },
                        { value: '1month', label: '1 month before' }
                      ]}
                      value={[
                        { value: '1day', label: '1 day before' },
                        { value: '2days', label: '2 days before' },
                        { value: '1week', label: '1 week before' },
                        { value: '2weeks', label: '2 weeks before' },
                        { value: '1month', label: '1 month before' }
                      ].find(opt => opt.value === versionRemindBefore) || null}
                      onChange={(option) => setVersionRemindBefore(option ? option.value : '')}
                      placeholder="Select reminder time"
                      isClearable
                      isSearchable
                    />
                  </Form.Group>
                </Col>
              )}

              {/* Document Issuing Authority */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-label">
                    Document Issuing Authority
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={versionIssuingAuthority}
                    onChange={(e) => setVersionIssuingAuthority(e.target.value)}
                    placeholder="Enter issuing authority"
                  />
                </Form.Group>
              </Col>

              {/* Other Comments */}
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-label">
                    Other Comments
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={versionOtherComments}
                    onChange={(e) => setVersionOtherComments(e.target.value)}
                    placeholder="Enter any additional comments"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="alert alert-info" style={{ fontSize: '0.875rem', marginTop: '1rem' }}>
              <strong>Note:</strong> Uploading a new version will create a new version of this document.
              The previous version will be preserved in the version history.
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="doc-form-cancel-btn"
            onClick={() => {
              setShowUploadVersionModal(false);
              setUploadVersionFile(null);
              // Reset form fields
              setVersionDocumentTitle('');
              setVersionRegisteredTo('');
              setVersionReferenceNumber('');
              setVersionIssueDate('');
              setVersionExpiryDate('');
              setVersionRemindBefore('');
              setVersionIssuingAuthority('');
              setVersionOtherComments('');
            }}
          >
            Cancel
          </Button>
          <Button
            className="doc-form-submit-btn"
            onClick={handleUploadVersion}
            disabled={!uploadVersionFile || uploadingVersion || !versionDocumentTitle.trim()}
          >
            {uploadingVersion ? 'Uploading...' : 'Upload Version'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revoke All Shares Confirmation Modal */}
      <Modal show={showRevokeAllModal} onHide={() => {
        setShowRevokeAllModal(false);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Revoke All Shares</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to revoke all shares for this document?</p>
          {documentShares?.is_shared && documentShares?.shares && (
            <div className="mt-3">
              <p className="mb-1"><strong>Document:</strong> {documentData?.document_title || documentData?.name || documentData?.file_name}</p>
              <div className="mt-2">
                <p className="mb-1"><strong>Current Shares:</strong></p>
                <ul className="mb-0" style={{ paddingLeft: '1.5rem' }}>
                  {documentShares.shares.branches && documentShares.shares.branches.length > 0 && (
                    <li>{documentShares.shares.branches.length} Branch(es)</li>
                  )}
                  {documentShares.shares.departments && documentShares.shares.departments.length > 0 && (
                    <li>{documentShares.shares.departments.length} Department(s)</li>
                  )}
                  {documentShares.shares.sub_departments && documentShares.shares.sub_departments.length > 0 && (
                    <li>{documentShares.shares.sub_departments.length} Sub-Department(s)</li>
                  )}
                  {documentShares.shares.users && documentShares.shares.users.length > 0 && (
                    <li>{documentShares.shares.users.length} User(s)</li>
                  )}
                </ul>
              </div>
            </div>
          )}
          <p className="mt-3 text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            className="doc-form-cancel-btn"
            onClick={() => {
              setShowRevokeAllModal(false);
            }}
            disabled={revokingAllShares}
          >
            Cancel
          </Button>
          <Button 
            className="doc-form-submit-btn"
            onClick={handleRevokeAllShares}
            disabled={revokingAllShares}
          >
            {revokingAllShares ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Revoking...
              </>
            ) : (
              'Revoke All Shares'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revoke Share Confirmation Modal */}
      <Modal show={showRevokeShareModal} onHide={() => {
        setShowRevokeShareModal(false);
        setRevokeShareData(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Revoke Share</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to revoke this share for this document?</p>
          {revokeShareData && documentData && (
            <div className="mt-3">
              <p className="mb-1"><strong>Document:</strong> {documentData?.document_title || documentData?.name || documentData?.file_name}</p>
              <p className="mb-0">
                <strong>Shared with:</strong> {revokeShareData.shareName}
                {revokeShareData.shareType && (
                  <span className="text-muted"> ({revokeShareData.shareType === 'sub_department' ? 'Sub-Department' : revokeShareData.shareType.charAt(0).toUpperCase() + revokeShareData.shareType.slice(1)})</span>
                )}
              </p>
            </div>
          )}
          <p className="mt-3 text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            className="doc-form-cancel-btn"
            onClick={() => {
              setShowRevokeShareModal(false);
              setRevokeShareData(null);
            }}
            disabled={revokingShare}
          >
            Cancel
          </Button>
          <Button 
            className="doc-form-submit-btn"
            onClick={handleRevokeShare}
            disabled={revokingShare}
          >
            {revokingShare ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Revoking...
              </>
            ) : (
              'Revoke Share'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Share Document Modal */}
      <ShareDocumentModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        fileId={documentData?.id}
        selectedBranches={selectedBranches}
        onBranchesChange={handleBranchChange}
        selectedDepartments={selectedDepartments}
        onDepartmentsChange={handleDepartmentChange}
        selectedSubDepartments={selectedSubDepartments}
        onSubDepartmentsChange={handleSubDepartmentChange}
        branches={branches}
        departments={departments}
        subDepartments={subDepartments}
        filteredDepartments={filteredDepartments}
        filteredSubDepartments={filteredSubDepartments}
        availableUsers={filteredUsers}
        selectedUsers={selectedUsers}
        onUsersChange={handleUserChange}
        onInternalShare={handleShareDocument}
        onEmailShare={handleEmailShareDocument}
        sharing={sharing}
        onReset={resetShareForm}
      />

      <InternalFooter />
    </React.Fragment>
  );
};

export default DocumentDetails;

